import React, { useEffect, Suspense } from 'react';

import { Route, Routes } from 'react-router-dom';
import { get, intersection } from 'lodash';

import { userState } from '../states/auth';
import { useLoadAllClientsCallback } from '../states/client';
import { authRepo } from '../repositories';
import { datadogRum } from '@datadog/browser-rum';

import { useRecoilState } from 'recoil';

import Header from '../components/Header';
import IncidentList from './IncidentList';
import IncidentDetailContainer from './IncidentDetail';
import ShipmentGeocodingContainer from './ShipmentGeocoding';
import CustomerProfileScreen from './CustomerProfileScreen';
import LandingPage from './LandingPage';
import ShipmentDetailContainer from './ShipmentDetail';
import { ToastBar, Toaster } from 'react-hot-toast';
import ValidateAddressContainer from './ValidateAddress';
import BeansAiContainer from './BeansAiContainer';
import DropoffLocationsHistory from './DropoffLocationsHistory';
import { SCOPES } from '../constants/scopes';

const routes = [
  { path: '/', component: LandingPage },
  { path: '/incident', component: LandingPage },
  { path: '/incident/:type', component: IncidentList },
  { path: '/incident/:type/page/:page', component: IncidentList },
  { path: '/incident/:type/:id', component: IncidentDetailContainer },
  { path: '/shipment/:id', component: ShipmentGeocodingContainer },
  { path: '/profile/:id', component: CustomerProfileScreen },
  { path: '/shipments/:id', component: ShipmentDetailContainer },
  { path: '/validate-address', component: ValidateAddressContainer },
  { path: '/dropoff-locations-history', component: DropoffLocationsHistory, scopes: [SCOPES.ADMIN, SCOPES.SUPER_ADMIN] },
];

function RootContainer() {
  const [user, setUser] = useRecoilState(userState);
  const loadClient = useLoadAllClientsCallback();
  const userScopes = get(user, 'user.scopes', []);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    authRepo
      .getUser({ signal: abortController.signal })
      .then((r) => {
        if (!isMounted) return;

        setUser(r.ok ? r.data : null);

        if (datadogRum && r.ok && r.data?.user) {
          datadogRum.setUser({
            id: r.data.user.id,
            name: r.data.user.name,
            scopes: r.data.user.scopes,
          });
        }

        if (!r.ok) {
          window.location = `${import.meta.env.REACT_APP_OAUTH_URL}/login?next=${encodeURI(window.location.href)}`;
        }
      })
      .catch((error) => {
        // Ignore abort errors, handle other authentication errors by redirecting to login
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          return;
        }
        if (isMounted) {
          window.location = `${import.meta.env.REACT_APP_OAUTH_URL}/login?next=${encodeURI(window.location.href)}`;
        }
      });

    loadClient();

    return () => {
      isMounted = false;
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return <div>Loading...</div>;

  return (
    <React.Fragment>
      <Toaster>
        {(t) => (
          <ToastBar
            toast={t}
            style={{}} // Overwrite styles
            position="top-center" // Used to adapt the animation
          />
        )}
      </Toaster>
      <Header user={user} />
      <Routes>
        {routes
          ?.filter((route) => !route.scopes || intersection(route.scopes, userScopes)?.length > 0)
          .map((route) => (
            <Route key={route.path} path={route.path} element={<route.component />} />
          ))}
        {String(import.meta.env.REACT_APP_USE_BEANS).toLowerCase() === 'true' && (
          <Route
            path={'/beans/:id'}
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <BeansAiContainer />
              </Suspense>
            }
          />
        )}
      </Routes>
    </React.Fragment>
  );
}

export default RootContainer;
